import React, { useState, useCallback, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { TimelineBar } from "./TimelineBar";
import { SegmentTable } from "./SegmentTable";
import type { TimeEditorProps, TimeData, TimeSegment } from "./types";

// Get default start time (current time rounded to nearest 15 min)
function getDefaultStartTime(): string {
  const now = new Date();
  const minutes = now.getMinutes();
  now.setMinutes(Math.round(minutes / 15) * 15, 0, 0);
  return now.toISOString();
}

// Parse value if it's a JSON string
function parseTimeData(value: TimeData | string | null | undefined): TimeData | null {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as TimeData;
    } catch {
      return null;
    }
  }
  return value;
}

export function EmbeddedTimeEditor({ value, hideTitle = true }: TimeEditorProps) {
  // Parse value in case it's a JSON string
  const parsedValue = parseTimeData(value);

  // Initialize start_time from value, or default to current time
  const [startTime, setStartTime] = useState<string>(() => {
    if (parsedValue?.start_time) {
      return parsedValue.start_time;
    }
    return getDefaultStartTime();
  });

  // Initialize segments from value, or empty array
  const [segments, setSegments] = useState<TimeSegment[]>(() => {
    if (parsedValue?.segments && Array.isArray(parsedValue.segments)) {
      return parsedValue.segments;
    }
    return [];
  });

  // Sync internal state when value prop changes (e.g. shift selected)
  useEffect(() => {
    const next = parseTimeData(value);
    if (next?.start_time) {
      setStartTime(next.start_time);
    }
    if (next?.segments && Array.isArray(next.segments)) {
      setSegments(next.segments);
    }
  }, [value]);

  // Track if segment table has an active menu
  const [isMenuActive, setIsMenuActive] = useState(false);

  // Stable callback for menu active state
  const handleMenuActive = useCallback((isActive: boolean) => {
    setIsMenuActive(isActive);
  }, []);

  return (
    <View style={[styles.container, {}]}>
      {hideTitle ? null : <ThemedText type="subtitle">Time Entry</ThemedText>}

      {/* Timeline visualization */}
      {segments.length > 0 && (
        <View style={[styles.section, { zIndex: 2, elevation: 2 }]}>
          <TimelineBar startTime={startTime} segments={segments} />
        </View>
      )}

      {/* Segments table */}
      <View style={[styles.section, isMenuActive && { zIndex: 2, elevation: 2 }]}>
        <SegmentTable
          startTime={startTime}
          segments={segments}
          onStartTimeChange={setStartTime}
          onSegmentsChange={setSegments}
          onMenuActive={handleMenuActive}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "transparent",
    padding: 8,
    gap: 8,
    minWidth: 490,
    maxWidth: 490,
    overflow: "visible",
  },
  section: {
    gap: 8,
    overflow: "visible",
  },
  footer: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 8,
    zIndex: -1,
    elevation: -1,
  },
  cancelButton: {
    height: 36,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  saveButton: {
    height: 36,
    paddingHorizontal: 16,
  },
});
